import { makeArticle } from "./learnFactory";
import type { LearnFAQ } from "./learnShared";
import type { RelatedLink as RL } from "@/components/RelatedPages";

/**
 * /learn/what-is-a-verified-saint-provider
 *
 * "Is it really SAINT®?" — how to verify a SAINT® clinic before you pay.
 * Mirrors the trust/verification content from the prior version but reframes
 * it around the 3-step check and the red flags that show up in "SAINT™"
 * articles selling a lower-cost, off-label accelerated TMS program.
 */
const faqs: LearnFAQ[] = [
  {
    question: `How do I verify a SAINT® provider?`,
    answer: `Find the clinic on Magnus Medical's official provider locator (magnusmedical.com/find-a-provider), ask whether the targeting software is FDA-cleared (510(k) K220177), and ask whether the exact protocol was tested in the SAINT clinical trials. A clinic that cannot confirm all three is not delivering the FDA-cleared SAINT® protocol.`,
  },
  {
    question: `What does "Verified SAINT® Provider" mean?`,
    answer: `A Verified SAINT® Provider is a clinic listed on Magnus Medical's official provider locator as authorized to deliver SAINT® on the Magnus Neuromodulation System, with manufacturer-certified training and the required fMRI-guided targeting technology. Neuro Wellness TMS Centers of America in Coral Springs, Florida is a verified SAINT® provider.`,
  },
  {
    question: `Can a clinic that isn't on the locator still offer SAINT®?`,
    answer: `No. A clinic offering "accelerated TMS" or "5-day TMS" without the Magnus Neuromodulation System and fMRI targeting is delivering an off-label schedule on a different device, not the FDA-cleared SAINT® protocol. Check the Magnus Medical locator and ask the questions above before you pay.`,
  },
];

export const pageWhatIsVerifiedProvider = makeArticle({
  slug: "what-is-a-verified-saint-provider",
  title: `Is It Really SAINT®? How to Verify a SAINT® TMS Clinic (2026)`,
  description: `Before you pay for SAINT®, verify the clinic: find it on Magnus Medical's provider locator, confirm FDA-cleared targeting, and ask whether the protocol was in the SAINT trials.`,
  canonical: "/learn/what-is-a-verified-saint-provider",
  h1: `Is It Really SAINT®? How to Tell Before You Pay`,
  breadcrumbLabel: "Verify a SAINT® Provider",
  shortAnswer: `SAINT® is delivered only on the FDA-cleared Magnus Neuromodulation System with fMRI-guided targeting, and only at clinics listed on Magnus Medical's official provider locator. Before you pay, confirm three things: (1) the clinic is on the Magnus Medical locator, (2) the targeting software is FDA-cleared (510(k) K220177), and (3) the protocol was tested in the SAINT clinical trials. Neuro Wellness TMS Centers of America in Coral Springs, Florida is a verified SAINT® provider. If you are in crisis, call or text 988.`,
  keyTakeaways: [
    `Verify a SAINT® clinic on Magnus Medical's official provider locator (magnusmedical.com/find-a-provider).`,
    `Confirm the targeting software is FDA-cleared (510(k) K220177).`,
    `Ask whether the exact protocol was tested in the SAINT clinical trials.`,
    `A clinic not on the locator is delivering an off-label accelerated schedule, not SAINT®.`,
    `Neuro Wellness in Coral Springs, FL is a verified SAINT® provider.`,
  ],
  sections: [
    {
      heading: "A 3-step check before you pay",
      paragraphs: [
        `Before you commit to any clinic that advertises SAINT®, run this 3-step check. It takes a few minutes and protects you from paying for a different treatment sold under SAINT's name.`,
      ],
      list: [
        `1. Find the clinic on Magnus Medical's official provider locator at magnusmedical.com/find-a-provider. If the clinic is not listed, it is not a verified SAINT® provider.`,
        `2. Ask whether the targeting software is FDA-cleared for personalized fMRI targeting. SAINT® uses the FDA-cleared Magnus Neuromodulation System (510(k) K220177, September 2022); other "fMRI-guided" programs use their own methods, which are not FDA-cleared for this purpose.`,
        `3. Ask whether the exact protocol was tested in the SAINT clinical trials. The 78.6% remission result (Cole et al., Am J Psychiatry 2022;179:132–141) comes from SAINT® only and does not transfer to a program that was not in the trial.`,
      ],
    },
    {
      heading: "Red flags in SAINT™ articles",
      paragraphs: [
        `Some articles use SAINT's name and trial numbers to sell a lower-cost, off-label "fMRI-guided accelerated TMS" program for about $9,900. Watch for these signs that the page is not describing SAINT®:`,
      ],
      list: [
        `A headline that says SAINT but then recommends an "alternative" or "similar" program.`,
        `A page that quotes SAINT's 79% remission rate (78.6% in the 2022 trial) next to a different program.`,
        `"SAINT-style", "SAINT-inspired", or "like SAINT" wording rather than the FDA-cleared SAINT®.`,
        `A price (~$9,900) bundled with extras like massage, red-light therapy, or physical therapy.`,
        `A clinic that cannot name the Magnus Neuromodulation System or show its Magnus Medical locator listing.`,
      ],
    },
    {
      heading: "Why verification matters",
      paragraphs: [
        `SAINT®'s fMRI-guided targeting is central to the protocol. A clinic that delivers "accelerated TMS" without the Magnus system and fMRI targeting is not delivering SAINT® — it is delivering an off-label accelerated schedule on a different device. The SAINT® trial results apply to the SAINT® system only. For why the targeting method itself matters, see [[BEAM F3 vs fMRI targeting|/learn/beam-f3-vs-fmri-tms]]. [[See the full side-by-side comparison|/learn/saint-vs-accelerated-tms]] or [[check SAINT® locations|/learn/saint-tms-locations]].`,
      ],
    },
  ],
  faqs,
  sources: [
    {
      citation:
        "Magnus Medical — SAINT® Provider Locator (magnusmedical.com/find-a-provider).",
      url: "https://www.magnusmedical.com/find-a-provider",
    },
    {
      citation:
        "U.S. Food and Drug Administration. 510(k) Premarket Notification — Magnus Neuromodulation System with SAINT Technology (K220177, September 2022).",
      url: "https://www.accessdata.fda.gov/scripts/cdrh/cfdocs/cfpmn/pmn.cfm",
    },
    {
      citation:
        "Cole EJ, et al. Stanford Neuromodulation Therapy (SNT): A Double-Blind Randomized Controlled Trial. Am J Psychiatry. 2022;179(2):132-141.",
      url: "https://pubmed.ncbi.nlm.nih.gov/35624717/",
    },
  ],
  relatedLinks: [
    {
      title: `SAINT\u00ae Disclaimer: Look-Alike Programs Are Not SAINT\u00ae`,
      href: "/learn/saint-disclaimer",
      description:
        "Our formal disclaimer: Cognitive FX and other \u201cfMRI-guided\u201d programs are not SAINT\u00ae",
    },
    {
      title: `SAINT\u00ae vs $9,900 "Accelerated TMS": Not the Same Treatment`,
      href: "/learn/saint-vs-accelerated-tms",
      description:
        "Full side-by-side: FDA clearance, targeting, evidence, price",
    },
    {
      title: "SAINT® TMS Locations",
      href: "/learn/saint-tms-locations",
      description: "Verified providers and how patients travel to Florida",
    },
    {
      title: "Find a Verified SAINT® Provider",
      href: "/find-a-verified-saint-provider",
      description: "Our official Magnus Medical listing",
    },
    {
      title: "What Is SAINT® TMS?",
      href: "/learn/what-is-saint-tms",
      description: "The FDA-cleared, fMRI-guided 5-day protocol explained",
    },
    {
      title: "Who Qualifies for SAINT® TMS?",
      href: "/learn/who-qualifies-for-saint-tms",
      description: "Eligibility, checklists, and next steps",
    },
    {
      title: "SAINT® Accelerated TMS",
      href: "/saint",
      description: "The 5-day, fMRI-guided protocol offered at our clinic",
    },
    {
      title: "BEAM F3 vs fMRI targeting",
      href: "/learn/beam-f3-vs-fmri-tms",
      description:
        "How head-measurement targeting differs from an fMRI-guided target",
    },
  ] as RL[],
  reviewBlock: {
    writtenBy: "Neuro Wellness TMS Centers of America",
    reviewedBy: "Daniel Pietras, MD",
    reviewedByHref: "/dr-daniel-pietras-md",
    published: "November 3, 2026",
    lastReviewed: "November 3, 2026",
    lastUpdated: "November 3, 2026",
  },
  toc: [
    { label: "A 3-step check", href: "#a-3-step-check-before-you-pay" },
    { label: "Red flags", href: "#red-flags-in-saint-articles" },
    { label: "Why verification matters", href: "#why-verification-matters" },
    { label: "FAQ", href: "#faq" },
    { label: "Cited research", href: "#cited-research" },
  ],
  safetyBlock:
    "SAINT® is not for everyone. It is contraindicated for people with non-removable magnetic-sensitive metal in or near the head, certain implanted devices, or increased intracranial pressure. Possible side effects include headache, fatigue, scalp discomfort and anxiety; TMS carries a small risk of seizure. If you are in crisis, call or text 988.",
  trademarkFooter:
    "SAINT® is a registered trademark of Stanford University, exclusively licensed to Magnus Medical, Inc. Neuro Wellness TMS Centers of America is not affiliated with Stanford University and does not name or endorse any other clinic or company.",
  ctaBand: {
    headline: "Verify before you pay — and start free",
    primaryText: "See if I may be a candidate",
    primaryHref: "/learn/who-qualifies-for-saint-tms",
    secondaryText: "Talk to a real person",
    secondaryHref: "tel:9548279793",
    phone: "954-827-9793",
  },
});
